import { FC, Fragment, useCallback, MouseEvent } from "react";
import {
  IconButton,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import { DashboardV2FolderMoveActionMenu } from "./DashboardV2FolderMoveActionMenu";
import { NavigationItemsVisibilityType } from "@/store/navigation/utils/types";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { useAppAbility } from "@/services/permissions";

interface DashboardV2FolderMoveActionProps {
  id: string;
  name: string;
  onOpen?(): void;
  onClose?(): void;
  iconButton?: boolean;
  containsProtectedItem?: boolean;
  visibility: NavigationItemsVisibilityType;
}

export const DashboardV2FolderMoveAction: FC<
  DashboardV2FolderMoveActionProps
> = ({
  id,
  name,
  onOpen,
  onClose,
  visibility,
  iconButton,
  containsProtectedItem,
}) => {
  const { can } = useAppAbility();
  const canEditDashboard = can("create", "dashboard");
  const { anchor, open, openMenu, closeMenu: closeMoveMenu } = useMenuHook();

  const title = iconButton ? "Move" : "";

  const closeMenusHandler = useCallback(() => {
    onClose?.();
    closeMoveMenu();
  }, [onClose, closeMoveMenu]);

  const openMenuHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement | HTMLLIElement>) => {
      onOpen?.();
      openMenu(event);
    },
    [onOpen, openMenu],
  );

  return (
    <Fragment>
      <Tooltip
        arrow
        title={
          containsProtectedItem
            ? "This folder contains protected dashboard(s) and cannot be moved."
            : title
        }
        placement={iconButton ? "top" : "right"}
      >
        <span>
          {iconButton ? (
            <IconButton
              size="small"
              sx={iconButtonStyles}
              disabled={containsProtectedItem || !canEditDashboard}
              onClick={openMenuHandler}
            >
              <DriveFileMoveOutlinedIcon fontSize="small" />
            </IconButton>
          ) : (
            <MenuItem
              onClick={openMenuHandler}
              sx={menuItemSx}
              disabled={containsProtectedItem || !canEditDashboard}
            >
              <ListItemIcon>
                <DriveFileMoveOutlinedIcon fontSize="small" sx={iconSx} />
              </ListItemIcon>
              <Typography variant="body2">Move</Typography>
              <ListItemIcon sx={{ justifyContent: "flex-end", ...iconSx }}>
                <ChevronRightOutlinedIcon fontSize="small" />
              </ListItemIcon>
            </MenuItem>
          )}
        </span>
      </Tooltip>

      {open && (
        <DashboardV2FolderMoveActionMenu
          open
          id={id}
          name={name}
          anchor={anchor}
          visibility={visibility}
          onClose={closeMenusHandler}
        />
      )}
    </Fragment>
  );
};

const iconSx = { minWidth: 28 };
const iconButtonStyles = { p: 0.3, "&:hover": { bgcolor: "grey.200" } };
const menuItemSx = {
  pl: 1,
  pr: 1.5,
};
